import React, { useState, useRef, useEffect, useMemo, useContext } from "react";
import {
  Box,
  Button,
  Container,
  Typography,
  IconButton,
  Paper,
  Dialog,
  AppBar,
  Toolbar,
  Slide,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import moment from "moment";
import currency from "currency.js";
import {
  getQueryParam,
  equalsIgnoreCase,
  getCustomerName,
  formatPhoneNumber,
  capitalize,
  getOptimizedCloudinaryUrl,
  getStoreFilterStreets,
  orderMatchesStoreFilter,
} from "../../common/util";
import { useLocation } from "react-router-dom";
import bkstApi from "../../api";
import { UserContext } from "../../context/UserContext";
import { useNavigate } from "react-router";
import SearchIcon from "@mui/icons-material/Search";
import BDatePicker from "../../common/input/BDatePicker";
import LinearProgress from "@mui/material/LinearProgress";
import ItemFilter from "../../planner/component/ItemFilter";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { IMG_PLACEHOLDER, STORAGE_BUCKET } from "../../common/constants";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const PLACEHOLDER_IMAGE = `${STORAGE_BUCKET}${IMG_PLACEHOLDER}`;
const CUSTOM_ITEM_PREFIX = "Custom Item | ";

function itemPhotoUrl(photo) {
  if (!photo) {
    return PLACEHOLDER_IMAGE;
  }
  return getOptimizedCloudinaryUrl(photo, "f_auto,q_70,w_80");
}

function ItemTitleCell({ title }) {
  if (title?.startsWith(CUSTOM_ITEM_PREFIX)) {
    const name = title.slice(CUSTOM_ITEM_PREFIX.length);
    return (
      <Box sx={{ py: 0.5, lineHeight: 1.4 }}>
        <Typography variant="body2" color="text.secondary" display="block">
          Custom Item
        </Typography>
        <Typography variant="body2" fontWeight={500}>
          {name}
        </Typography>
      </Box>
    );
  }
  return (
    <Typography variant="body2" fontWeight={500} sx={{ py: 0.5 }}>
      {title}
    </Typography>
  );
}

export default function ItemSalesReport() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useContext(UserContext);

  const [fromDt, setFromDt] = useState("");
  const [toDt, setToDt] = useState("");

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [itemFilter, setItemFilter] = useState({});
  const [storeFilter, setStoreFilter] = useState({});
  const [filtered, setFiltered] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const storeLocations = user?.config?.storeLocation || [];
  const storeStreets = getStoreFilterStreets(user);
  const showStoreFilter = storeStreets.length > 1;

  const summaryApiRef = useRef(null);
  const dataGridApiRef = useRef(null);

  useEffect(() => {
    let [qFrom, qTo] = [
      getQueryParam(location.search, "from"),
      getQueryParam(location.search, "to"),
    ];
    if (!qFrom || !qTo) {
      qFrom = moment().format("YYYY-MM-DD");
      qTo = moment().format("YYYY-MM-DD");
    }
    search(qFrom, qTo);
  }, [location.search]);

  useEffect(() => {
    if (toDt < fromDt) {
      setToDt("");
    } else if (fromDt && toDt && moment(toDt).diff(moment(fromDt), "days") > 31) {
      setToDt("");
    }
  }, [fromDt, toDt]);

  const runDateSearch = (e) => {
    e.preventDefault();
    navigate(`/report/item-sales?from=${fromDt}&to=${toDt}`);
  };

  const search = (fromDate, toDate) => {
    setLoading(true);
    setError("");
    bkstApi(`/my/report/item-sales?fromDt=${fromDate}&toDt=${toDate}`)
      .then((res) => {
        setOrders(res.data.orders || []);
        setFromDt(fromDate);
        setToDt(toDate);
        setLoading(false);
      })
      .catch((e) => {
        setError(e.response?.data?.error || e.message || "Failed to load item sales");
        setLoading(false);
      });
  };

  const exportSummaryToCSV = () => {
    if (summaryApiRef.current) {
      summaryApiRef.current.exportDataAsCsv({
        fileName: `Item_Sales_Summary_${fromDt}_${toDt}`,
        fields: ["title", "count", "sales"],
      });
    }
  };

  const exportToCSV = () => {
    if (dataGridApiRef.current) {
      const categoryName = selectedCategory?.title?.replace(/\s+/g, "_") || "";
      dataGridApiRef.current.exportDataAsCsv({
        fileName: `Item_Sales_${fromDt}_${toDt}_${categoryName}`,
        allColumns: true,
      });
    }
  };

  const visibleOrders = useMemo(() => {
    return (orders || []).filter((o) =>
      orderMatchesStoreFilter(o, storeFilter?.store?.value, storeLocations)
    );
  }, [orders, storeFilter, storeLocations]);

  const rows = useMemo(() => {
    if (!visibleOrders || visibleOrders.length === 0) return [];

    const flatItems = [];
    let rowIndex = 0;

    visibleOrders.forEach((order) => {
      if (order.items && order.items.length > 0) {
        const customerName = getCustomerName(order.customer) || "";
        const customerPhone = order.customer?.phone || "";

        order.items.forEach((item) => {
          let notes = item.variant?.notes?.name;
          if (!notes && item.description) {
            notes = item.description.replace(/<[^>]*>?/g, "");
          }

          const itemPhoto = item.images?.[0] || "";

          flatItems.push({
            id: `${order.id}-${item.productId || item.title}-${rowIndex++}`,
            orderId: order.id,
            quantity: item.quantity,
            itemName: item.title,
            type: item.productId ? "standard" : "custom",
            productId: item.productId || "",
            total: Number(item.total) || Number(item.price) || 0,
            photo: itemPhoto,
            variant: item.variant || {},
            customerName,
            customerPhone,
            itemNotes: notes,
          });
        });
      }
    });

    return flatItems;
  }, [visibleOrders]);

  const summaryRows = useMemo(() => {
    const dataSource = filtered && filtered.length > 0 ? filtered : rows;

    if (!dataSource || dataSource.length === 0) return [];

    const groupedData = {};

    dataSource.forEach((row) => {
      const groupKey = row.productId || row.itemName;

      if (!groupedData[groupKey]) {
        groupedData[groupKey] = {
          id: groupKey,
          groupKey,
          productId: row.productId || "",
          itemName: row.itemName,
          title: row.productId
            ? capitalize(row.itemName)
            : `${CUSTOM_ITEM_PREFIX}${row.itemName}`,
          photo: "",
          count: 0,
          sales: 0,
        };
      }

      groupedData[groupKey].count += Number(row.quantity) || 0;
      groupedData[groupKey].sales += Number(row.total) || 0;
      if (row.productId && row.photo && !groupedData[groupKey].photo) {
        groupedData[groupKey].photo = row.photo;
      }
    });

    return Object.values(groupedData).sort((a, b) => b.sales - a.sales);
  }, [rows, filtered]);

  const detailRows = useMemo(() => {
    if (!selectedCategory) return [];

    const categoryRows = rows.filter((row) => {
      const rowGroupKey = row.productId || row.itemName;
      return rowGroupKey === selectedCategory.groupKey;
    });

    if (Object.keys(itemFilter).length > 0) {
      return categoryRows.filter((row) => {
        for (const [key, filterValue] of Object.entries(itemFilter)) {
          if (filterValue?.value) {
            const variantValue = row.variant?.[key];
            let valueToMatch = "";

            if (typeof variantValue === "object" && variantValue?.name) {
              valueToMatch = String(variantValue.name);
            } else if (variantValue !== null && variantValue !== undefined) {
              valueToMatch = String(variantValue);
            }

            if (!equalsIgnoreCase(valueToMatch, filterValue.value)) {
              return false;
            }
          }
        }
        return true;
      });
    }

    return categoryRows;
  }, [rows, selectedCategory, itemFilter]);

  const detailTotal = useMemo(
    () => detailRows.reduce((sum, row) => sum + Number(row.quantity), 0),
    [detailRows]
  );

  const detailSalesTotal = useMemo(
    () => detailRows.reduce((sum, row) => sum + Number(row.total), 0),
    [detailRows]
  );

  const detailVariantKeys = useMemo(() => {
    if (!detailRows || detailRows.length === 0) return [];

    const keys = new Set();
    detailRows.forEach((row) => {
      if (row.variant) {
        Object.keys(row.variant).forEach((key) => {
          const value = row.variant[key];
          if (value !== null && value !== undefined && value !== "" && key !== "notes") {
            keys.add(key);
          }
        });
      }
    });

    return Array.from(keys);
  }, [detailRows]);

  const summaryColumns = [
    {
      field: "photo",
      headerName: "",
      width: 72,
      sortable: false,
      disableExport: true,
      renderCell: (params) => {
        const photo = params.row.productId ? params.row.photo : null;
        return (
          <Box
            component="img"
            src={itemPhotoUrl(photo)}
            alt=""
            sx={{
              width: 48,
              height: 48,
              borderRadius: 1,
              objectFit: "cover",
              bgcolor: "#f5f5f5",
              display: "block",
              boxShadow: 1,
            }}
          />
        );
      },
    },
    {
      field: "count",
      headerName: "Qty",
      width: 75,
      type: "number",
    },
    {
      field: "title",
      headerName: "Item",
      flex: 2,
      minWidth: 200,
      renderCell: (params) => <ItemTitleCell title={params.row.title} />,
    },
    {
      field: "sales",
      headerName: "Sales",
      width: 120,
      type: "number",
      align: "right",
      headerAlign: "right",
      valueFormatter: ({ value }) => currency(value || 0).format(),
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={600}>
          {currency(params.row.sales || 0).format()}
        </Typography>
      ),
    },
  ];

  const detailColumns = useMemo(() => {
    const customerNameColumn = {
      field: "customerName",
      headerName: "Customer Name",
      hide: true,
    };

    const customerPhoneColumn = {
      field: "customerPhone",
      headerName: "Customer Phone",
      hide: true,
      valueFormatter: (params) => {
        const phone = params.value || "";
        return ` ${formatPhoneNumber(phone)}`;
      },
    };

    const itemNotesColumn = {
      field: "itemNotes",
      headerName: "Notes",
      hide: true,
    };

    const itemNameColumn = {
      field: "itemName",
      headerName: "Item Name",
      flex: 1,
      minWidth: 200,
    };

    const variantColumns = detailVariantKeys.map((key) => ({
      field: `variant_${key}`,
      headerName: key
        .split("-")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" "),
      flex: 1,
      minWidth: 120,
      valueGetter: (params) => {
        const variant = params.row.variant || {};
        const variantValue = variant[key];

        if (!variantValue) return "";

        if (typeof variantValue === "object") {
          return variantValue.name || "";
        }

        return variantValue;
      },
    }));

    const quantityHeader = detailTotal > 0 ? `Qty (${detailTotal})` : "Qty";
    const quantityColumn = {
      field: "quantity",
      headerName: quantityHeader,
      width: 100,
      type: "number",
    };

    const salesHeader =
      detailSalesTotal > 0 ? `Sales (${currency(detailSalesTotal).format()})` : "Sales";
    const salesColumn = {
      field: "total",
      headerName: salesHeader,
      width: 130,
      type: "number",
      align: "right",
      headerAlign: "right",
      renderCell: (params) => currency(params.row.total || 0).format(),
    };

    const orderLinkColumn = {
      field: "orderId",
      headerName: "",
      width: 50,
      sortable: false,
      disableExport: true,
      renderCell: (params) => (
        <IconButton component="a" href={`/order/${params.row.orderId}`} target="_blank">
          <OpenInNewIcon fontSize="small" />
        </IconButton>
      ),
    };

    return [
      quantityColumn,
      itemNameColumn,
      ...variantColumns,
      salesColumn,
      orderLinkColumn,
      itemNotesColumn,
      customerNameColumn,
      customerPhoneColumn,
    ];
  }, [detailVariantKeys, detailTotal, detailSalesTotal]);

  useEffect(() => {
    let filteredRows = rows;

    if (Object.keys(itemFilter).length > 0) {
      filteredRows = filteredRows.filter((row) => {
        for (const [key, filterValue] of Object.entries(itemFilter)) {
          if (filterValue?.value) {
            if (key === "item") {
              if (!equalsIgnoreCase(row.itemName, filterValue.value)) {
                return false;
              }
            } else {
              const variantValue = row.variant?.[key];
              let valueToMatch = "";

              if (typeof variantValue === "object" && variantValue?.name) {
                valueToMatch = String(variantValue.name);
              } else if (variantValue !== null && variantValue !== undefined) {
                valueToMatch = String(variantValue);
              }

              if (!equalsIgnoreCase(valueToMatch, filterValue.value)) {
                return false;
              }
            }
          }
        }
        return true;
      });
    }

    setFiltered(filteredRows);
  }, [rows, itemFilter]);

  const filterConfig = useMemo(() => {
    if (!selectedCategory) return [];

    const filterOptionsMap = {};
    const dataSource = detailRows.length > 0 ? detailRows : [];
    const priorityOrder = ["size", "flavor", "filling", "icing"];

    dataSource.forEach((row) => {
      if (row.variant) {
        Object.entries(row.variant).forEach(([key, value]) => {
          if (key === "notes" || key === "inscription") {
            return;
          }

          if (!filterOptionsMap[key]) {
            filterOptionsMap[key] = new Set();
          }

          let filterValue = "";
          if (typeof value === "object" && value?.name) {
            filterValue = value.name;
          } else if (value !== null && value !== undefined) {
            filterValue = value;
          }

          if (filterValue) {
            filterOptionsMap[key].add(String(filterValue));
          }
        });
      }
    });

    const allFilters = Object.entries(filterOptionsMap)
      .map(([key, valueSet]) => ({
        name: key,
        options: Array.from(valueSet),
        key,
      }))
      .filter((config) => config.options.length > 0);

    const priorityFilters = allFilters.filter((f) => priorityOrder.includes(f.key));
    const otherFilters = allFilters.filter((f) => !priorityOrder.includes(f.key));

    priorityFilters.sort((a, b) => {
      const indexA = priorityOrder.indexOf(a.key);
      const indexB = priorityOrder.indexOf(b.key);
      return indexA - indexB;
    });

    otherFilters.sort((a, b) => b.options.length - a.options.length);

    return [...priorityFilters, ...otherFilters];
  }, [selectedCategory, detailRows]);

  if (loading) {
    return <LinearProgress />;
  }

  return (
    <Container maxWidth="md">
      <Box my={4}>
        <Typography variant="h1" gutterBottom>
          Item Sales Report
        </Typography>
        <Typography variant="subtitle1" color="textSecondary" gutterBottom>
          A high-level summary of item sales for the selected date range. Click on any item to see
          an itemized view with variant details and filter options.
        </Typography>
        <Box my={3}>
          <form onSubmit={runDateSearch}>
            <Box sx={{ display: "flex", gap: "10px" }}>
              <BDatePicker
                value={fromDt}
                variant="filled"
                label="From Date"
                onChange={(d) => setFromDt(d)}
                required
                autoOk
                hideIcon={true}
                disableFuture
                minDate={moment().subtract(1, "year").toDate()}
              />
              <BDatePicker
                value={toDt}
                variant="filled"
                label="To Date"
                onChange={(d) => setToDt(d)}
                required
                autoOk
                hideIcon={true}
                disableFuture
                minDate={fromDt ? moment(fromDt).toDate() : undefined}
                maxDate={fromDt ? moment(fromDt).add(31, "days").toDate() : undefined}
              />
              <Button
                variant="contained"
                color="primary"
                type="submit"
                style={{ height: "56px" }}
              >
                <SearchIcon />
              </Button>
            </Box>
          </form>
        </Box>
      </Box>

      {showStoreFilter && orders && orders.length > 0 && (
        <Box my={2}>
          <ItemFilter
            config={[{ name: "store", options: storeStreets }]}
            data={orders}
            onChange={(o) => setStoreFilter({ ...storeFilter, ...o })}
          />
        </Box>
      )}

      {error ? (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      ) : null}

      {rows.length > 0 ? (
        <>
          <Box my={2}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
                Summary
              </Typography>
              <IconButton onClick={exportSummaryToCSV}>
                <FileDownloadOutlinedIcon />
              </IconButton>
            </Box>
            <Box sx={{ height: "auto", width: "100%" }}>
              <DataGrid
                apiRef={summaryApiRef}
                rows={summaryRows}
                columns={summaryColumns}
                autoHeight
                getRowHeight={() => "auto"}
                disableRowSelectionOnClick
                disableColumnMenu
                initialState={{
                  sorting: {
                    sortModel: [{ field: "sales", sort: "desc" }],
                  },
                }}
                onRowClick={(params) => {
                  setSelectedCategory(params.row);
                  setItemFilter({});
                }}
                sx={{
                  "& .MuiDataGrid-cell": {
                    borderBottom: "1px solid #ECECEC",
                    paddingY: "10px",
                  },
                  "& .MuiDataGrid-row": {
                    cursor: "pointer",
                  },
                  "& .MuiDataGrid-row:nth-of-type(odd)": {
                    backgroundColor: "#FAFAFA",
                  },
                  "& .MuiDataGrid-row:nth-of-type(even)": {
                    backgroundColor: "#FFF",
                  },
                  "& .MuiDataGrid-cell:focus": {
                    outline: "none",
                  },
                  "& .MuiDataGrid-cell:focus-within": {
                    outline: "none",
                  },
                }}
              />
            </Box>
          </Box>

          <Dialog
            fullScreen
            open={Boolean(selectedCategory)}
            onClose={() => {
              setSelectedCategory(null);
              setItemFilter({});
            }}
            TransitionComponent={Transition}
          >
            <AppBar sx={{ position: "relative" }}>
              <Toolbar>
                <IconButton
                  edge="start"
                  color="inherit"
                  onClick={() => {
                    setSelectedCategory(null);
                    setItemFilter({});
                  }}
                  aria-label="back"
                >
                  <ArrowBackIcon />
                </IconButton>
                <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                  {selectedCategory?.title?.startsWith(CUSTOM_ITEM_PREFIX)
                    ? selectedCategory.title.slice(CUSTOM_ITEM_PREFIX.length)
                    : selectedCategory?.title}{" "}
                  ({detailTotal} units, {currency(detailSalesTotal).format()}) |{" "}
                  {moment(fromDt).format("MMM D")} - {moment(toDt).format("MMM D")}
                </Typography>
                <IconButton color="inherit" onClick={exportToCSV} title="Export to CSV">
                  <FileDownloadOutlinedIcon />
                </IconButton>
                <IconButton
                  color="inherit"
                  onClick={() => {
                    setSelectedCategory(null);
                    setItemFilter({});
                  }}
                  aria-label="close"
                >
                  <CloseIcon />
                </IconButton>
              </Toolbar>
            </AppBar>
            <Box sx={{ p: 2 }}>
              <Paper elevation={0}>
                <Box py={0.75}>
                  <ItemFilter
                    config={filterConfig}
                    onChange={(filterUpdate) => {
                      setItemFilter({ ...itemFilter, ...filterUpdate });
                    }}
                  />
                </Box>
              </Paper>
              <Box sx={{ height: "auto", width: "100%" }}>
                <DataGrid
                  apiRef={dataGridApiRef}
                  rows={detailRows}
                  columns={detailColumns}
                  autoHeight
                  getRowHeight={() => "auto"}
                  disableRowSelectionOnClick
                  disableColumnMenu
                  initialState={{
                    sorting: {
                      sortModel: [{ field: "itemName", sort: "asc" }],
                    },
                    columns: {
                      columnVisibilityModel: {
                        customerName: false,
                        customerPhone: false,
                        itemNotes: false,
                      },
                    },
                  }}
                  sx={{
                    "& .MuiDataGrid-cell": {
                      borderBottom: "1px solid #ECECEC",
                      paddingY: "10px",
                    },
                    "& .MuiDataGrid-columnHeaders": {
                      backgroundColor: "#ECECEC",
                    },
                    "& .MuiDataGrid-row:nth-of-type(odd)": {
                      backgroundColor: "#FAFAFA",
                    },
                    "& .MuiDataGrid-row:nth-of-type(even)": {
                      backgroundColor: "#FFF",
                    },
                    "& .MuiDataGrid-cell:focus": {
                      outline: "none",
                    },
                    "& .MuiDataGrid-cell:focus-within": {
                      outline: "none",
                    },
                  }}
                />
              </Box>
            </Box>
          </Dialog>
        </>
      ) : (
        !error && (
          <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
            No item sales found for this date range.
          </Typography>
        )
      )}
    </Container>
  );
}
